<script setup lang="ts">
import type { ECharts, EChartsOption } from "echarts"
import * as echarts from "echarts"
import "echarts-wordcloud"
import "@p-v/libs/bmapgl"
// import "echarts-gl"
import { debounce, defaultsDeep, cloneDeep } from "lodash-es"
import * as pkg from "resize-detector"
import dark from "./dark"
import { ref, inject, watch, onMounted, onUnmounted } from "vue"
// import mapData from "china-map-echarts"
const { removeListener, addListener } = pkg

const props = withDefaults(defineProps<{ deepUpdate?: boolean; option: EChartsOption; style: { [key: string]: string } }>(), {
  option: () => ({}),
  style: () => ({}),
  deepUpdate: () => false,
})

echarts.registerTheme("dark-chart", dark)

// const { map_100000 } = mapData

// echarts.registerMap("china", map_100000)

const chartDom = ref<HTMLDivElement>()
let chart: ECharts | null = null
let chartInstance = ref<ECharts>()
const emptyPlaceholder = {
  id: "empty_placeholder",
  type: "rect",
  left: "center",
  top: "middle",
  silent: true,
  textContent: {
    type: "text",
    style: {
      text: "暂无数据",
      fill: "rgba(163, 166, 173, 0.64)",
      fontFamily: "Microsoft YaHei",
      fontSize: "14px",
      textAlign: "center",
      textVerticalAlign: "middle",
    },
    invisible: false,
  },
  textConfig: {
    position: "inside",
    left: "center",
    top: "middle",
  },
  shape: {
    x: 10,
    y: 10,
    width: 100,
    height: 100,
    r: 18,
  },
  style: {
    fill: "rgba(255, 255, 255, 0)",
  },
}
const isRealDark = inject("isRealDark", ref(false))
function resizeChart() {
  chart?.resize()
}

const resize = debounce(resizeChart, 300)

function disposeChart() {
  if (chartDom.value) removeListener(chartDom.value, resize)

  chart?.dispose()
  chart = null
}

function initChart() {
  disposeChart()
  if (chartDom.value) {
    chart = echarts.init(chartDom.value, isRealDark.value ? "dark-chart" : undefined)
    chartInstance.value = chart
    emptyPlaceholder.shape.width = chart.getWidth()
    emptyPlaceholder.shape.height = chart.getHeight()
    if (!props.option) return false
    setOption(props.option)
    addListener(chartDom.value, resize)
    // init echarts
  }
}

function getZr() {
  return chart?.getZr()
}

function getModel() {
  return (chart as any)?.getModel()
}

const setOption = (option: EChartsOption) => {
  if (props.deepUpdate) chart?.clear()
  const newOpt = cloneDeep(option)
  if (option.graphic && (option.graphic as echarts.GraphicComponentOption[]).length > 0) {
    emptyPlaceholder.textContent.invisible = option.graphic[0].invisible
    if (!emptyPlaceholder.textContent.invisible) {
      newOpt.series = []
      if (newOpt.title) {
        newOpt.title.show = false
      }
    }
  }
  chart?.setOption(defaultsDeep({ graphic: [emptyPlaceholder] }, newOpt))
}

watch(
  isRealDark,
  () => {
    initChart()
  },
  {
    flush: "post",
  },
)

watch(
  () => props.option,
  () => {
    setOption(props.option)
  },
  {
    deep: true,
    flush: "post",
  },
)

onMounted(() => {
  initChart()
})

onUnmounted(() => {
  disposeChart()
})

defineExpose({
  chartDom,
  chartInstance,
  getZr,
  getModel,
})
</script>

<template>
  <view ref="chartDom" class="chart-container" :style="style" />
</template>

<style lang="scss" scoped>
.chart-container {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
